import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowsClockwise, Trash, SignIn, Warning, Lock, PencilSimple } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOORT_LABEL = {
  login_gelukt: "Aangemeld",
  login_mislukt: "Mislukte poging",
  geblokkeerd: "Geblokkeerd",
  wijziging: "Wijziging",
};

const SOORT_STIJL = {
  login_gelukt: "bg-[#E6F4EA] text-[#1B5E20]",
  login_mislukt: "bg-[#FFF4E5] text-[#8A4B00]",
  geblokkeerd: "bg-[#FDE7E7] text-[#B71C1C]",
  wijziging: "bg-[#E8EEFF] text-[#002FA7]",
};

const tijd = (iso) => (iso ? new Date(iso).toLocaleString("nl-BE", { dateStyle: "short", timeStyle: "short" }) : "—");

export default function Logboek() {
  const [items, setItems] = useState([]);
  const [totalen, setTotalen] = useState({});
  const [gebruikers, setGebruikers] = useState([]);
  const [soort, setSoort] = useState("alles");
  const [dagen, setDagen] = useState("30");
  const [zoek, setZoek] = useState("");
  const [laden, setLaden] = useState(true);
  const [wisOpen, setWisOpen] = useState(false);
  const [wissen, setWissen] = useState(false);

  const laad = useCallback(async () => {
    setLaden(true);
    try {
      const [log, users] = await Promise.all([
        axios.get(`${API}/logboek`, { params: { soort, dagen, zoek: zoek || undefined } }),
        axios.get(`${API}/logboek/gebruikers`),
      ]);
      setItems(log.data.items || []);
      setTotalen(log.data.totalen || {});
      setGebruikers(users.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Logboek laden mislukt");
    } finally { setLaden(false); }
  }, [soort, dagen, zoek]);

  useEffect(() => {
    const timer = setTimeout(() => { laad(); }, zoek ? 400 : 0);
    return () => clearTimeout(timer);
  }, [laad, zoek]);

  const wis = async () => {
    setWissen(true);
    try {
      const res = await axios.delete(`${API}/logboek`, { params: { soort } });
      toast.success(res.data.message);
      setWisOpen(false);
      laad();
    } catch (e) { toast.error(e.response?.data?.detail || "Wissen mislukt"); }
    finally { setWissen(false); }
  };

  const kaarten = [
    { k: "login_gelukt", l: "Aanmeldingen", icon: SignIn },
    { k: "login_mislukt", l: "Mislukte pogingen", icon: Warning },
    { k: "geblokkeerd", l: "Blokkades", icon: Lock },
    { k: "wijziging", l: "Wijzigingen", icon: PencilSimple },
  ];

  return (
    <div className="space-y-6" data-testid="logboek-pagina">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="page-title">Logboek</h1>
          <p className="text-sm text-muted-foreground mt-1">Aanmeldingen, mislukte pogingen en wijzigingen — enkel voor de superadmin</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-sm" onClick={laad} disabled={laden} data-testid="logboek-ververs">
            <ArrowsClockwise size={15} weight="bold" className="mr-1.5" /> Verversen
          </Button>
          <Button className="rounded-sm bg-[#C62828] hover:bg-[#A31F1F] text-white" onClick={() => setWisOpen(true)} data-testid="logboek-wis-btn">
            <Trash size={15} weight="bold" className="mr-1.5" /> Wissen
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {kaarten.map(({ k, l, icon: Icon }) => (
          <div key={k} className="bg-white border border-border rounded-sm p-4" data-testid={`logboek-kaart-${k}`}>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Icon size={16} weight="bold" />
              <p className="text-[11px] uppercase tracking-wider font-bold">{l}</p>
            </div>
            <p className="text-2xl font-black mt-1" style={{ fontFamily: "Chivo, sans-serif" }}>{totalen[k] ?? 0}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="gebeurtenissen" className="w-full">
        <TabsList className="rounded-sm grid grid-cols-2 max-w-md gap-1 p-1" data-testid="logboek-tabs">
          <TabsTrigger value="gebeurtenissen" className="rounded-sm text-xs font-semibold" data-testid="tab-gebeurtenissen">Gebeurtenissen</TabsTrigger>
          <TabsTrigger value="gebruikers" className="rounded-sm text-xs font-semibold" data-testid="tab-per-gebruiker">Per gebruiker</TabsTrigger>
        </TabsList>

        <TabsContent value="gebeurtenissen" className="mt-4 space-y-3">
          <div className="flex flex-wrap gap-2">
            <Select value={soort} onValueChange={setSoort}>
              <SelectTrigger className="rounded-sm w-[200px]" data-testid="logboek-filter-soort"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="alles">Alle gebeurtenissen</SelectItem>
                <SelectItem value="login_gelukt">Aanmeldingen</SelectItem>
                <SelectItem value="login_mislukt">Mislukte pogingen</SelectItem>
                <SelectItem value="geblokkeerd">Blokkades</SelectItem>
                <SelectItem value="wijziging">Wijzigingen</SelectItem>
              </SelectContent>
            </Select>
            <Select value={dagen} onValueChange={setDagen}>
              <SelectTrigger className="rounded-sm w-[150px]" data-testid="logboek-filter-dagen"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Vandaag</SelectItem>
                <SelectItem value="7">7 dagen</SelectItem>
                <SelectItem value="30">30 dagen</SelectItem>
                <SelectItem value="180">180 dagen</SelectItem>
              </SelectContent>
            </Select>
            <Input
              placeholder="Zoek op naam, e-mail of actie..."
              value={zoek}
              onChange={(e) => setZoek(e.target.value)}
              className="rounded-sm w-[280px]"
              data-testid="logboek-zoek"
            />
          </div>

          <div className="bg-white border border-border rounded-sm overflow-x-auto">
            <table className="w-full text-sm border-collapse min-w-[760px]">
              <thead>
                <tr className="bg-[#E0FFFF]">
                  <th className="px-3 py-2 text-left font-bold border-b border-[#999]">Tijdstip</th>
                  <th className="px-3 py-2 text-left font-bold border-b border-[#999]">Soort</th>
                  <th className="px-3 py-2 text-left font-bold border-b border-[#999]">Gebruiker</th>
                  <th className="px-3 py-2 text-left font-bold border-b border-[#999]">Firma</th>
                  <th className="px-3 py-2 text-left font-bold border-b border-[#999]">Actie</th>
                  <th className="px-3 py-2 text-left font-bold border-b border-[#999]">IP</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} className="hover:bg-muted/20" data-testid={`logboek-rij-${r.id}`}>
                    <td className="px-3 py-2 border-b border-border text-xs whitespace-nowrap">{tijd(r.tijdstip)}</td>
                    <td className="px-3 py-2 border-b border-border">
                      <span className={`text-[11px] font-bold px-2 py-0.5 rounded-sm ${SOORT_STIJL[r.soort] || ""}`}>
                        {SOORT_LABEL[r.soort] || r.soort}
                      </span>
                    </td>
                    <td className="px-3 py-2 border-b border-border text-xs">
                      <span className="font-semibold">{r.user_naam || "—"}</span>
                      <span className="block text-muted-foreground">{r.email}</span>
                    </td>
                    <td className="px-3 py-2 border-b border-border text-xs">{r.firma_naam || "—"}</td>
                    <td className="px-3 py-2 border-b border-border text-xs">{r.omschrijving}</td>
                    <td className="px-3 py-2 border-b border-border text-xs text-muted-foreground">{r.ip || "—"}</td>
                  </tr>
                ))}
                {!laden && items.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-8 text-center text-sm text-muted-foreground" data-testid="logboek-leeg">Geen gebeurtenissen gevonden</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="gebruikers" className="mt-4">
          <div className="bg-white border border-border rounded-sm overflow-x-auto">
            <table className="w-full text-sm border-collapse min-w-[760px]">
              <thead>
                <tr className="bg-[#E0FFFF]">
                  <th className="px-3 py-2 text-left font-bold border-b border-[#999]">Gebruiker</th>
                  <th className="px-3 py-2 text-left font-bold border-b border-[#999]">Firma</th>
                  <th className="px-3 py-2 text-left font-bold border-b border-[#999]">Rol</th>
                  <th className="px-3 py-2 text-left font-bold border-b border-[#999]">Laatste aanmelding</th>
                  <th className="px-3 py-2 text-center font-bold border-b border-[#999]">Mislukt</th>
                  <th className="px-3 py-2 text-center font-bold border-b border-[#999]">Wijzigingen</th>
                </tr>
              </thead>
              <tbody>
                {gebruikers.map((u) => (
                  <tr key={u.id} className="hover:bg-muted/20" data-testid={`logboek-gebruiker-${u.id}`}>
                    <td className="px-3 py-2 border-b border-border text-xs">
                      <span className="font-semibold">{u.naam || u.email}</span>
                      <span className="block text-muted-foreground">{u.email}</span>
                      {u.geblokkeerd && <span className="text-[11px] font-bold text-[#C62828]">Geblokkeerd</span>}
                    </td>
                    <td className="px-3 py-2 border-b border-border text-xs">{u.firma_naam || "—"}</td>
                    <td className="px-3 py-2 border-b border-border text-xs capitalize">{u.rol}</td>
                    <td className="px-3 py-2 border-b border-border text-xs whitespace-nowrap">
                      {tijd(u.laatste_login)}
                      {u.laatste_ip && <span className="block text-muted-foreground">IP {u.laatste_ip}</span>}
                    </td>
                    <td className="px-3 py-2 border-b border-border text-xs text-center font-bold">{u.mislukte_pogingen}</td>
                    <td className="px-3 py-2 border-b border-border text-xs text-center">{u.aantal_wijzigingen}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <AlertDialog open={wisOpen} onOpenChange={setWisOpen}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Logboek wissen?</AlertDialogTitle>
            <AlertDialogDescription>
              {soort === "alles"
                ? "Alle logboekregels worden definitief verwijderd."
                : `Alle regels van soort "${SOORT_LABEL[soort]}" worden definitief verwijderd.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" data-testid="logboek-wis-annuleer">Annuleren</AlertDialogCancel>
            <AlertDialogAction className="rounded-sm bg-[#C62828] hover:bg-[#A31F1F]" onClick={wis} disabled={wissen} data-testid="logboek-wis-bevestig">
              {wissen ? "Wissen..." : "Definitief wissen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
